import { API_URL } from '../constants';
import { apiFetch } from './client';

export interface IFormSubmissionType {
    id: number;
    label: string;
}

export interface ICustomerFormSubmissionsQueryParams {
    page?: number;
    limit?: number;
    sortDirection?: 'ASC' | 'DESC';
    typeId?: number | null;
    email?: string | null;
    processed?: boolean | null;
    createdAtMin?: string | null;
    createdAtMax?: string | null;
}

const getAuthHeaders = () => {
    return {
        'Content-Type': 'application/json',
    };
};

const handleResponse = async (res: Response) => {
    if (!res.ok) {
        throw new Error('Server Error!');
    }
    return await res.json();
};

export const stringifyCustomerFormSubmissionsParameters = (
    params: ICustomerFormSubmissionsQueryParams
): string => {
    const query: string[] = [];
    try {
        if (params.page) query.push(`page=${params.page}`);
        if (params.limit) query.push(`limit=${params.limit}`);
        if (params.sortDirection) query.push(`sortDirection=${params.sortDirection}`);
        if (params.typeId != null) query.push(`typeId=${params.typeId}`);
        if (params.email) query.push(`email=${encodeURIComponent(params.email)}`);
        if (params.processed != null) query.push(`processed=${params.processed}`);
        if (params.createdAtMin) query.push(`createdAtMin=${params.createdAtMin}`);
        if (params.createdAtMax) query.push(`createdAtMax=${params.createdAtMax}`);
    } catch (e) {
        console.warn('Error building customer form submissions query params');
    }
    return query.join('&');
};

export const getAllCustomerFormSubmissions = async (
    queryParams: ICustomerFormSubmissionsQueryParams
) => {
    const res = await apiFetch(
        `${API_URL}/aav/customer-form-submissions?${stringifyCustomerFormSubmissionsParameters(queryParams)}`,
        { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(res);
};

export const getCustomerFormSubmissionById = async (submissionId: number | string) => {
    const res = await apiFetch(`${API_URL}/aav/customer-form-submissions/${submissionId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    const data = await handleResponse(res);
    return normalizeCustomerFormSubmission(data);
};

export const updateCustomerFormSubmission = async (
    submissionId: number | string,
    data: { processed: boolean }
) => {
    const res = await apiFetch(`${API_URL}/aav/customer-form-submissions/${submissionId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    return handleResponse(res);
};

export const deleteCustomerFormSubmission = async (submissionId: number | string) => {
    const res = await apiFetch(`${API_URL}/aav/customer-form-submissions/${submissionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
};

export const normalizeFormSubmissionTypes = (payload: unknown): IFormSubmissionType[] => {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object') {
        const obj = payload as Record<string, unknown>;
        if (Array.isArray(obj.result)) return obj.result as IFormSubmissionType[];
        if (Array.isArray(obj.data)) return obj.data as IFormSubmissionType[];
    }
    return [];
};

export const normalizeCustomerFormSubmission = (payload: unknown): Record<string, any> | null => {
    if (!payload || typeof payload !== 'object') return null;
    const obj = payload as Record<string, unknown>;
    if (obj.result && typeof obj.result === 'object' && !Array.isArray(obj.result)) {
        return obj.result as Record<string, any>;
    }
    return obj as Record<string, any>;
};

export const WHOLESALE_REQUEST_TYPE_ID = 2;

export const getAllFormSubmissionTypes = async () => {
    const res = await apiFetch(`${API_URL}/aav/form-submission-types`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    const data = await handleResponse(res);
    return normalizeFormSubmissionTypes(data);
};

export const getFormSubmissionTypeById = async (typeId: number | string) => {
    const res = await apiFetch(`${API_URL}/aav/form-submission-types/${typeId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
};

export const createFormSubmissionType = async (data: { id: number; label?: string }) => {
    const res = await apiFetch(`${API_URL}/aav/form-submission-types`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    return handleResponse(res);
};

export const deleteFormSubmissionType = async (typeId: number | string) => {
    const res = await apiFetch(`${API_URL}/aav/form-submission-types/${typeId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
};
